import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { Button, Spin, Dropdown, Menu as AntMenu } from 'antd';
import { MenuOutlined, UserOutlined, LoginOutlined } from '@ant-design/icons';


const WidgetForm = ({
  config,
  orgHours,
  tables,
  reservations,
  onSubmit,
  orgId,
  prefill,
  onCancel,
  refetchReservations,
  onViewMenu,
  onValidationSuccess,
  authMode = false,
  onGoDashboard,
}) => {
  // ------------------ State ------------------
  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    partySize: config.minGuests,
    date: new Date(),
    time: '',
    notes: '',
    occasion: '',
    email: '',
  });
const cdnUrl = 'https://netshine-assets.blr1.digitaloceanspaces.com/company_61/logos/company_logo_61_1751699306600';

  const [collisionError, setCollisionError] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalPhone, setModalPhone] = useState('');
  const [modalEmail, setModalEmail] = useState('');
  const [verificationMethod, setVerificationMethod] = useState('phone');
  const [modalStep, setModalStep] = useState('contact');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState(null);
  const [resendMessage, setResendMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [isValidationOnly, setIsValidationOnly] = useState(false);

  const isEdit = !!prefill;


  // ------------------ Effects ------------------
useEffect(() => {
  if (prefill) {
    setFormData({
      id: prefill.id,  // ✅ carry the reservation id
      customerName: prefill.customerName,
      phone: prefill.phone,
      partySize: prefill.partySize,
      date: new Date(prefill.start),
      time: new Date(prefill.start).toTimeString().slice(0, 5),
      notes: prefill.notes,
      occasion: prefill.occasion,
      email: prefill.email,
    });
  }
}, [prefill]);

// Trigger modal immediately if WidgetForm opened in authMode
useEffect(() => {
  if (authMode && !isModalVisible) {
    setIsModalVisible(true);
    setModalStep("contact");
  }
}, [authMode]);

useEffect(() => {
  if (isModalVisible) {
    // Keep existing step if user already reached OTP
    setModalStep((prev) => (['otp', 'success'].includes(prev) ? prev : 'contact'));
    setModalPhone((p) => formData.phone || p);
    setModalEmail((e) => formData.email || e);
    setOtpError(null);
    setResendMessage(null);
  }
}, [isModalVisible, formData.phone, formData.email]);

// ✅ When WidgetForm is opened in authMode (Sign In flow), show login modal automatically
useEffect(() => {
  if (authMode) {
    setIsModalVisible(true);
    setModalStep("contact");
  }
}, [authMode]);

  // ------------------ Auth Handlers ------------------
  const getMaskedContact = () => {
    if (verificationMethod === 'phone') {
      const phone = modalPhone || formData.phone;
      if (!phone) return '';
      return phone.replace(/\d(?=\d{3})/g, 'X');
    } else {
      const email = modalEmail || formData.email;
      if (!email) return '';
      const [user, domain] = email.split('@');
      return `${user[0]}***${user.slice(-1)}@${domain}`;
    }
  };


  
  const guestOptions = Array.from({ length: config.maxGuests - config.minGuests + 1 }, (_, i) => ({
    value: i + config.minGuests,
    label: i + config.minGuests,
  }));

  const occasionOptions = [
    { value: '', label: 'Select an occasion (optional)' },
    { value: 'Birthday', label: 'Birthday' },
    { value: 'Anniversary', label: 'Anniversary' },
    { value: 'Date night', label: 'Date night' },
    { value: 'Business Meal', label: 'Business Meal' },
    { value: 'Celebration', label: 'Celebration' },
  ];


  
  const handleContactSubmit = () => {
    if (verificationMethod === 'phone' && !(modalPhone || formData.phone)) {
      setCollisionError('Phone number is required.');
      return;
    } else if (verificationMethod === 'email' && !(modalEmail || formData.email)) {
      setCollisionError('Email is required.');
      return;
    }
    setModalStep('otp');
    setResendMessage(null);
    setOtp('');
    setOtpError(null);
  };

const handleOtpSubmit = async () => {
  setIsSubmitting(true);
  if (otp !== '1234') {
    setOtpError('Invalid OTP');
    setIsSubmitting(false);
    return;
  }

  const phone = verificationMethod === 'phone' ? (modalPhone || formData.phone) : formData.phone;
  const email = verificationMethod === 'email' ? (modalEmail || formData.email) : formData.email;

  localStorage.setItem(
    'verifiedContact',
    JSON.stringify({
      method: verificationMethod,
      value: verificationMethod === 'phone' ? phone : email,
      timestamp: moment().toISOString(),
    })
  );

  setIsSubmitting(false);
  setModalStep('success');
setTimeout(() => {
  setIsModalVisible(false);
  if (isValidationOnly) {
    onValidationSuccess && onValidationSuccess();
  } else {
    onSubmit(formData);
  }
}, 900);
  setIsModalVisible(false); // ✅ close modal

  if (isValidationOnly) {
    onValidationSuccess(); // dashboard flow
  } else {
    onSubmit(formData); // reservation flow
  }
};


  const isUserVerified = () => {
  const data = localStorage.getItem("verifiedContact");
  if (!data) return false;
  try {
    const parsed = JSON.parse(data);
    // optional: check expiry (example: 24h validity)
    const age = moment().diff(moment(parsed.timestamp), "hours");
    return age < 24; 
  } catch {
    return false;
  }
};


  const resendOtp = () => {
    setResendMessage('OTP has been resent.');
    setTimeout(() => setResendMessage(null), 3000);
    setOtp('');
    setOtpError(null);
  };

  const handleSwitchToEmail = () => {
    setVerificationMethod('email');
    setModalStep('contact');
  };

  const handleSwitchToPhone = () => {
    setVerificationMethod('phone');
    setModalStep('contact');
  };

  const handleViewReservations = () => {
    setIsValidationOnly(true);
    setIsModalVisible(true);
  };

  // ------------------ Reservation Logic ------------------
  const timeSlots = useMemo(() => {
    if (!orgHours) return [];
    const slots = [];
    const now = new Date();
    const selectedDate = formData.date;
    const isToday = moment(selectedDate).isSame(now, 'day');

    let current = new Date(moment(selectedDate).format('YYYY-MM-DD') + ' ' + orgHours.openTime);
    const close = new Date(moment(selectedDate).format('YYYY-MM-DD') + ' ' + orgHours.closeTime);

    while (current <= close) {
      const slotTime = current.toTimeString().slice(0, 5);
      if (!isToday || current > now) slots.push(slotTime);
      current.setMinutes(current.getMinutes() + orgHours.slotMinutes);
    }
    return slots;
  }, [orgHours, formData.date]);

  const timeOptionObjects = timeSlots.map(slot => ({ value: slot, label: slot }));

  const checkCollision = (values, ignoreId) => {
    const start = new Date(`${new Date(values.date).toISOString().split('T')[0]} ${values.time}`);
    const end = new Date(start.getTime() + 60 * 60000);
    return reservations.some(
      (res) =>
        res.id !== ignoreId &&
        start < new Date(res.end) &&
        new Date(res.start) < end
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData({ ...formData, [name]: selectedOption ? selectedOption.value : '' });
  };

  const handleDateChange = (date) => {
    setFormData({ ...formData, date, time: '' });
  };



const handleFormSubmit = async (formData) => {
  try {
    const apiURL = "http://localhost:4000/api";
    const token = localStorage.getItem("token");

    const payload = {
      guest_name: formData.customerName,
      contact_number: formData.phone,
      email: formData.email,
      reservation_date: moment(formData.date).format("YYYY-MM-DD"),
      reservation_time: formData.time.length === 5 ? `${formData.time}:00` : formData.time,
      party_size: formData.partySize,
      table_preference: formData.tablePreference || null,
      special_requests: formData.notes || null,
      reservation_source: "widget",
      occasion: formData.occasion || null,
      status: config.autoConfirmation ? "confirmed" : "pending",
    };

    let response;

    if (formData.id) {
      // ✅ Edit / Reschedule
      response = await axios.put(
        `${apiURL}/pos/reservations/edit/${formData.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } else {
      // ✅ New Reservation
      response = await axios.post(
        `${apiURL}/pos/reservations/add`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }

    // ✅ Decide edit/new based on id presence
onSubmit(response.data); // ✅ ReservationWidget doesn’t care about edit flag now

  } catch (error) {
    console.error("Reservation save error:", error);
    alert("Failed to save reservation. Please try again.");
  }
};


  // ------------------ Menu ------------------
  const menu = (
    <AntMenu>
      {!prefill && (
        <AntMenu.Item key="dashboard" icon={<UserOutlined />} onClick={handleViewReservations}>
          My Dashboard
        </AntMenu.Item>
      )}
      <AntMenu.Item key="menu" icon={<MenuOutlined />} onClick={onViewMenu}>
        View Menu
      </AntMenu.Item>
      <AntMenu.Item
  key="login"
  icon={<LoginOutlined />}
  onClick={() => {
    if (isUserVerified()) {
      onValidationSuccess(); // skip straight to dashboard
    } else {
      setIsModalVisible(true);
    }
  }}
>
  Login
</AntMenu.Item>

    </AntMenu>
  );



  // ------------------ RESERVATION FORM RENDER ------------------
  return (
    <div className="reservation-widget">
      
      <div className="flex justify-between items-center mb-4">
        <h2 className="widget-title">{isEdit ? 'Reschedule Your Table' : config?.headerText || 'Reserve a Table'}</h2>
       
      </div>
      {collisionError && <div className="alert alert-danger">{collisionError}</div>}
      <div className="widget-body">
         <form
  id="reservation-form"
  className="reservation-form"
  onSubmit={(e) => {
    e.preventDefault(); // stop page reload
    handleFormSubmit(formData); // pass your stateful data
  }}
>
 
          {config?.requiredFields?.includes('name') && (
            <div className="mb-3">
           
              <input
                id="customerName"
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                placeholder="Name"
                className="form-control"
                required
                aria-required="true"
              />
            </div>
          )}
          {config?.requiredFields?.includes('phone') && (
            <div className="mb-3">

              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone"
                className="form-control"
                required
                aria-required="true"
              />
            </div>
          )}
          {config?.requiredFields?.includes('email') && (
            <div className="mb-3">
   
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className="form-control"
                required
                aria-required="true"
              />
            </div>
          )}
          {config?.requiredFields?.includes('guests') && (
            <div className="mb-3">

              <Select
                id="partySize"
                options={guestOptions}
                value={guestOptions.find((o) => o.value === formData.partySize)}
                onChange={(val) => handleSelectChange('partySize', val)}
                className="react-select"
                placeholder="Party Size"
                required
                aria-required="true"
              />
            </div>
          )}
          {config?.showTimePicker && (
            <>
              <div className="mb-3">

                <DatePicker
                  id="datePicker"
                  selected={formData.date}
                  onChange={handleDateChange}
                  minDate={new Date()}
                  maxDate={new Date(new Date().setDate(new Date().getDate() + (config?.maxAdvance || 30)))}
                  placeholderText="Select a date"
                  className="form-control datepicker"
                  required
                  aria-required="true"
                />
              </div>
              <div className="mb-3">

                <Select
                  id="timeSelect"
                  options={timeOptionObjects}
                  value={timeOptionObjects.find((o) => o.value === formData.time) || null}
                  onChange={(val) => handleSelectChange('time', val)}
                  className="react-select"
                  placeholder="Select a time"
                  required
                  aria-required="true"
                />
              </div>
            </>
          )}
          {config?.allowSpecialRequests && (
            <>
              <div className="mb-3">
      
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Special Requests"
                  className="form-control"
                  rows="3"
                  aria-describedby="notesHelp"
                />
                <small id="notesHelp" className="form-text text-muted">
                  Optional: Add any special requests for your reservation.
                </small>
              </div>
              <div className="mb-3">
   
                <Select
                  id="occasion"
                  options={occasionOptions}
                  value={occasionOptions.find((o) => o.value === formData.occasion)}
                  onChange={(val) => handleSelectChange('occasion', val)}
                  className="react-select"
                  placeholder="Select an occasion (optional)"
                  aria-describedby="occasionHelp"
                />
                <small id="occasionHelp" className="form-text text-muted">
                  Optional: Select the occasion for your reservation.
                </small>
              </div>
            </>
          )}
        </form>
<aside className="reservation-info" role="complementary" aria-label="Restaurant Information">
  {/* Logo */}
  {config?.showLogo && config?.company_logo && (
    <div className="company-logo">
      <img
        src={`https://netshine-assets.blr1.digitaloceanspaces.com/${config.company_logo}`}
        alt={config?.company_name || "Company Logo"}
      />
    </div>
  )}

  {/* Contact Info: prefer custom content if available */}
  {config?.contactInfoContent ? (
    <div
      className="contact-info"
      dangerouslySetInnerHTML={{ __html: config.contactInfoContent }}
    />
  ) : (
    <>
      {config?.company_name && <h3 className="company-name">{config.company_name}</h3>}
      {config?.company_address && <p className="info-line">📍 {config.company_address}</p>}
      {config?.company_phone && <p className="info-line">📞 {config.company_phone}</p>}
    </>
  )}

  {/* Working Hours */}
  {orgHours?.openTime && orgHours?.closeTime && (
    <div className="hours-section">
      <h4>🕒 Working Hours</h4>
      <ul className="hours-list">
        <li>
          <strong>All Days:</strong> {orgHours.openTime} – {orgHours.closeTime}
        </li>
      </ul>
    </div>
  )}

  <style jsx>{`
    .reservation-info {
      background: #f9fafb;
      padding: 20px;
      border-radius: 8px;
      border: 1px solid #eee;
      font-size: 0.9rem;
      color: #374151;
    }

    .company-logo {
      text-align: center;
      margin-bottom: 16px;
    }

    .company-logo img {
      max-width: 180px;
      max-height: 110px;
      object-fit: contain;
    }

    .company-name {
      font-size: 1.2rem;
      font-weight: 700;
      margin-bottom: 6px;
      color: #111827;
      text-align: center;
    }

    .info-line {
      margin: 4px 0;
      line-height: 1.4;
      font-size: 0.95rem;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .hours-section {
      margin-top: 16px;
    }

    .hours-section h4 {
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 6px;
    }

    .hours-list {
      list-style: none;
      margin: 0;
      padding: 0;
      font-size: 0.9rem;
      line-height: 1.5;
    }
  `}</style>
</aside>

      </div>
<div className="form-footer">
  <button
    type="submit"
    form="reservation-form"
    className="btn btn-primary"
    disabled={isSubmitting}
    aria-busy={isSubmitting}
  >
    {isSubmitting ? <Spin size="small" /> : isEdit ? 'Update Reservation' : 'Reserve Now'}
  </button>

  <button
    type="button"
    onClick={() => {
      if (isEdit) {
        onGoDashboard();   // ✅ go back to dashboard
      } else {
        onCancel();        // ✅ close popup for new reservation
      }
    }}
    className="btn btn-secondary"
    disabled={isSubmitting}
  >
    Cancel
  </button>
</div>



 {isModalVisible && !authMode && (
 <div className="modal show" role="dialog">
    <div className="modal-dialog">
      <div className="modal-content">
        <div className="modal-header">
<h5 className="modal-title">
  {authMode
    ? 'Login to Continue'
    : isValidationOnly
    ? 'Login to Manage Reservations'
    : 'Verify Your Reservation'}
</h5>

              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setIsModalVisible(false);
                  if (authMode) {
                    // Go back to main reservation mode
                    onCancel?.();
                  }
                }}
              />

        </div>
        <div className="modal-body">
          {modalStep === 'contact' ? (
            <>
              <p>Enter your {verificationMethod === 'phone' ? 'phone' : 'email'}:</p>
              <input
                type={verificationMethod === 'phone' ? 'tel' : 'email'}
                className="form-control mb-2"
                value={verificationMethod === 'phone' ? modalPhone : modalEmail}
                onChange={(e) =>
                  verificationMethod === 'phone'
                    ? setModalPhone(e.target.value)
                    : setModalEmail(e.target.value)
                }
              />
              <Button type="primary" onClick={handleContactSubmit}>Continue</Button>
            </>
          ) : (
            <>
              <p>OTP sent to {getMaskedContact()}:</p>
              <input
                type="text"
                className="form-control mb-2"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
              />
              {otpError && <div className="alert alert-danger">{otpError}</div>}
              <Button type="primary" onClick={handleOtpSubmit}>Verify</Button>
              <Button type="link" onClick={resendOtp}>Resend OTP</Button>
            </>
          )}

          {modalStep === 'success' && (
  <div style={{ textAlign: 'center', padding: '20px' }}>
    <h3>✅ Verified Successfully!</h3>
    <p>You can now continue booking or view your dashboard.</p>
  </div>
)}

        </div>
      </div>
    </div>
  </div>
)}

      <style jsx>{`
        .reservation-widget {
          font-family: ${config?.font || 'Open Sans'};
          background-color: ${config?.colors?.background || '#fff'};
          padding: 24px;
          border-radius: 8px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 99%;
          margin: 0 auto;
          color: ${config?.colors?.text || '#000'};
          position: relative;
        }
        .reservation-widget .widget-title {
          font-size: ${config?.headerFontSize || '1.5rem'};
          font-weight: 900;
          color: ${config?.colors?.primary || '#2563eb'};
          margin-bottom: 24px;
          text-align: center;
        }
        .reservation-widget .alert.alert-danger {
          background-color: #fee2e2;
          color: #b91c1c;
          padding: 12px;
          border-radius: 4px;
          margin-bottom: 16px;
          border: 1px solid #f87171;
        }
        .reservation-widget .alert.alert-success {
          background-color: #dcfce7;
          color: #166534;
          padding: 12px;
          border-radius: 4px;
          border: 1px solid #86efac;
        }
        .reservation-widget .widget-body {
          display: flex;
          gap: 24px;
          margin-bottom: 80px; /* space for sticky footer */
        }
        .reservation-widget .reservation-form {
          flex: 2;              /* equal share */
          max-width: 65%;       /* don’t let it exceed half */
          display: flex;
          flex-direction: column;
        }
        .reservation-widget .reservation-info {
          flex: 1;              /* equal share */
          max-width: 35%;
          background: #f9fafb;
            padding: 12px 16px;  /* ✅ Less padding */
            border-left: 1px solid #eee;
            font-size: 0.85rem;  /* ✅ Slightly smaller font */
          line-height: 1.4; 
        }
        .reservation-widget .form-footer {
          position: sticky;
          bottom: 0;
          background: #fff;
          padding: 12px 16px;
          border-top: 1px solid #eee;
          display: flex;
          justify-content: center;  /* ✅ center align buttons */
          gap: 16px;                /* ✅ spacing between buttons */
          z-index: 10;
        }

        .reservation-widget .hours-list {
          padding: 0;
          margin: 0;
          list-style: none;
        }
        .reservation-widget .mb-3 {
          margin-bottom: 16px;
        }
        .reservation-widget .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 500;
          color: ${config?.colors?.text || '#000'};
        }
        .reservation-widget .form-control {
          padding: 10px;
          border: 1px solid ${config?.colors?.muted || '#d1d5db'};
          border-radius: 4px;
          font-size: 1rem;
          width: 100%;
          box-sizing: border-box;
          background-color: ${config?.colors?.card || '#fff'};
          color: ${config?.colors?.text || '#000'};
           border-radius: 8px;
            padding: 12px 14px;
            border: 1px solid #d1d5db;
            background: #fff;
            transition: all 0.2s ease;
        }
        .reservation-widget .form-control:focus {
          border-color: ${config?.colors?.primary || '#2563eb'};
          outline: none;
          box-shadow: 0 0 0 2px rgba(${parseInt((config?.colors?.primary || '#2563eb').slice(1, 3), 16)},${parseInt((config?.colors?.primary || '#2563eb').slice(3, 5), 16)},${parseInt((config?.colors?.primary || '#2563eb').slice(5, 7), 16)},0.2);
        }
        .reservation-widget .react-select {
          width: 100%;
        }
        .reservation-widget .react-select__control {
          border: 1px solid ${config?.colors?.muted || '#d1d5db'};
          border-radius: 4px;
          font-size: 1rem;
        }
        .reservation-widget .react-select__control--is-focused {
          border-color: ${config?.colors?.primary || '#2563eb'};
          box-shadow: none;
        }
        .reservation-widget .datepicker {
          width: 100%;
        }
        .reservation-widget textarea.form-control {
          resize: vertical;
          height: 80px;
        }
        .reservation-widget .btn.btn-primary {
          background-color: ${config?.colors?.primary || '#2563eb'};
          color: ${config?.colors?.buttonText || '#fff'};
          padding: 12px;
          border: none;
          border-radius: 4px;
          font-size: 1rem;
          cursor: pointer;
          transition: background-color 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .reservation-widget .btn.btn-primary:hover {
          background-color: ${config?.colors?.buttonHover || '#1d4ed8'};
        }
        .reservation-widget .btn.btn-primary:disabled {
          background-color: #cccccc;
          cursor: not-allowed;
        }
        .reservation-widget .btn.btn-secondary {
          background-color: ${config?.colors?.muted || '#6b7280'};
          color: ${config?.colors?.text || '#000'};
          padding: 12px;
          border: none;
          border-radius: 4px;
          font-size: 1rem;
          cursor: pointer;
          transition: background-color 0.3s;
          margin-left: 8px;
        }
        .reservation-widget .btn.btn-secondary:hover {
          background-color: #4b5563;
        }
        .reservation-widget .btn.btn-link {
          padding: 0;
          background: none;
          border: none;
          color: ${config?.colors?.primary || '#2563eb'};
          cursor: pointer;
          font-size: 0.875rem;
          text-decoration: underline;
        }
        .reservation-widget .btn.btn-link:hover {
          color: ${config?.colors?.buttonHover || '#1d4ed8'};
        }
        .reservation-widget .alert.alert-info {
          background-color: #dbeafe;
          color: #1e40af;
          padding: 12px;
          border-radius: 4px;
          margin-top: 8px;
          border: 1px solid #bfdbfe;
        }
        .reservation-widget .modal {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
background-color: rgba(255, 255, 255, 0.3);

          z-index: 1050;
          overflow: auto;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .reservation-widget .modal.show {
          display: flex;
        }
        .reservation-widget .modal-dialog {
          width: 100%;
          max-width: 400px;
          margin: 0 auto;
        }
        .reservation-widget .modal-content {
          background-color: ${config?.colors?.card || '#fff'};
          border: 1px solid ${config?.colors?.muted || '#d1d5db'};
          border-radius: 4px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          min-height: 250px;
        }
        .reservation-widget .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          border-bottom: 1px solid ${config?.colors?.muted || '#d1d5db'};
        }
        .reservation-widget .modal-title {
          font-size: 1.25rem;
          font-weight: 500;
          color: ${config?.colors?.text || '#000'};
          margin: 0;
        }
        .reservation-widget .btn-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: ${config?.colors?.muted || '#6b7280'};
        }
        .reservation-widget .btn-close:hover {
          color: ${config?.colors?.text || '#000'};
        }
        .reservation-widget .modal-body {
          padding: 16px;
          color: ${config?.colors?.text || '#000'};
        }
        .reservation-widget .modal-body p {
          margin-bottom: 16px;
        }
        .reservation-widget .modal-body .form-control.mb-2 {
          margin-bottom: 16px;
        }
        .reservation-widget .modal-footer {
          display: flex;
          justify-content: flex-end;
          padding: 16px;
          border-top: 1px solid ${config?.colors?.muted || '#d1d5db'};
        }
        .reservation-widget-loading {
          text-align: center;
          padding: 20px;
          font-family: ${config?.font || 'Playfair Display, serif'};
          color: ${config?.colors?.text || '#000'};
        }
        .reservation-widget-error {
          text-align: center;
          padding: 20px;
          font-family: ${config?.font || 'Playfair Display, serif'};
          color: #b91c1c;
        }
  

  .reservation-widget .alert {
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 0.9rem;
}

  .reservation-widget .alert-danger {
  background-color: #fee2e2;
  color: #b91c1c;
}

@media (max-width: 768px) {
  .reservation-widget .widget-body {
    flex-direction: column;
  }
  .reservation-widget .reservation-form,
  .reservation-widget .reservation-info {
    max-width: 100%;   /* full width on small screens */
  }
}
        @media (max-width: 600px) {
          .reservation-widget,
          .auth-widget {
            padding: 16px;
            max-width: 100%;
          }
          .reservation-widget .modal-dialog {
            max-width: 90%;
          }
          .react-datepicker {
            font-size: 0.8rem !important;
            width: 100% !important;
          }
          .react-datepicker__month-container {
            width: 100% !important;
          }
          .react-datepicker__day {
            margin: 0.2rem;
            width: 1.5rem;
            line-height: 1.5rem;
          }
          .react-datepicker__header {
            padding-top: 0.4rem;
          }
          .react-datepicker__month {
            margin: 0.2rem;
          }
        }
        .widget-body {
      display: flex;
      gap: 24px;
      margin-bottom: 80px; /* space for sticky footer */
    }
    .reservation-form {
      flex: 2;
    }
    .reservation-info {
      flex: 1;
      background: #f9fafb;
      padding: 16px;
      border-left: 1px solid #eee;
      font-size: 0.9rem;
    }
    .form-footer {
      position: sticky;
      bottom: 0;
      background: #fff;
      padding: 12px 16px;
      border-top: 1px solid #eee;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      z-index: 10;
    }
    .hours-list {
      padding: 0;
      margin: 0;
      list-style: none;
    }

    .company-logo {
  display: flex;
  justify-content: center;   /* center horizontally */
  align-items: center;       /* center vertically if needed */
  margin-bottom: 16px;
}
     .company-logo img {
  max-width: 200px;          /* keep logo from being too large */
  max-height: 80px;          /* constrain height */
  height: auto;
  width: auto;
  object-fit: contain;       /* keep aspect ratio */
}

    @media (max-width: 768px) {
      .widget-body {
        flex-direction: column;
      }
      .reservation-info {
        border-left: none;
        border-top: 1px solid #eee;
      }
    }  
      `}</style>
    </div>
    
  );
};

export default WidgetForm;
